import React, { useState, useEffect } from 'react';
import useSWR from 'swr';
import { 
  CheckCircle2, Clock, Calendar, RotateCcw, Layers, ArrowRight, ChevronDown, ChevronUp, AlertCircle, Trash2, Search, Zap, Loader2
} from 'lucide-react';
import api from '../services/api';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { useToast } from '../context/ToastContext';
import CustomDropdown from '../components/CustomDropdown';
import ConfirmationModal from '../components/ConfirmationModal';
import { useNavigate } from 'react-router-dom';
import { useProject } from '../context/ProjectContext';
import Skeleton from '../components/Skeleton';

const fetcher = url => api.get(url).then(res => res.data);

const SprintHistoryCardSkeleton = () => (
  <div className="bg-white/[0.03] border border-white/10 rounded-3xl p-8 relative overflow-hidden flex flex-col h-fit">
    <div className="flex justify-between items-start mb-10">
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Skeleton width="12px" height="12px" borderRadius="50%" />
          <Skeleton width="180px" height="24px" />
        </div>
        <Skeleton width="220px" height="20px" borderRadius="0.5rem" />
      </div>
      <div className="flex flex-col items-end gap-5">
        <div className="w-10 h-10 rounded-xl bg-white/5" />
        <div className="space-y-2">
          <Skeleton width="60px" height="32px" />
          <Skeleton width="80px" height="12px" />
        </div>
      </div>
    </div>
    <div className="space-y-8">
      <Skeleton width="100%" height="10px" borderRadius="1rem" />
      <div className="grid grid-cols-2 md:flex md:items-center gap-8 md:gap-12">
        {[1, 2, 3].map(i => (
          <div key={i} className="flex flex-col gap-2">
            <Skeleton width="60px" height="10px" />
            <div className="flex items-center gap-3">
              <Skeleton width="32px" height="32px" borderRadius="0.75rem" />
              <Skeleton width="40px" height="24px" />
            </div>
          </div>
        ))}
      </div>
      <Skeleton width="100%" height="48px" borderRadius="1rem" />
    </div>
  </div>
);

const HistoryPageSkeleton = ({ t }) => (
  <div className="flex flex-col">
    <header className="flex-none mb-8 flex flex-col md:flex-row md:items-end justify-between gap-6 px-2">
      <div>
        <div className="flex items-center gap-4">
          <Skeleton width="32px" height="32px" borderRadius="50%" />
          <Skeleton width="200px" height="40px" />
        </div>
        <div className="mt-8">
          <Skeleton width="320px" height="52px" borderRadius="1rem" />
        </div>
      </div>
    </header>
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 pb-8">
      {[1, 2, 3, 4].map(i => <SprintHistoryCardSkeleton key={i} />)}
    </div>
  </div>
);

const TicketHistorySkeleton = () => (
  <div className="bg-white/5 border border-white/10 p-6 rounded-2xl flex md:flex-row flex-col gap-6 justify-between items-start md:items-center">
    <div className="flex flex-col gap-2 flex-1 w-full">
      <div className="flex items-center gap-3">
        <Skeleton width="80px" height="18px" borderRadius="0.5rem" />
        <Skeleton width="60%" height="20px" borderRadius="0.5rem" />
      </div>
      <div className="flex items-center gap-4 mt-2">
        <Skeleton width="150px" height="12px" />
        <Skeleton width="100px" height="24px" borderRadius="0.5rem" />
      </div>
    </div>
  </div>
);

const History = () => {
  const { t, lang } = useLanguage();
  const { addToast } = useToast();
  const { activeProjectId, projects: teams, filteredProjects, canAccess, loading: isLoadingTeams, isInitialized } = useProject();
  const navigate = useNavigate();
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [expandedSprintId, setExpandedSprintId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [confModal, setConfModal] = useState({ isOpen: false, onConfirm: () => {} });

  // Synchronize local selectedTeamId with global activeProjectId
  useEffect(() => {
    if (!canAccess('/history')) {
      navigate('/projects');
      return;
    }
    if (activeProjectId) {
      setSelectedTeamId(activeProjectId);
    } else if (teams.length > 0) {
      setSelectedTeamId(teams[0]._id);
    }
  }, [activeProjectId, teams, canAccess, navigate]);

  // SWR for Tickets (Include deleted for history)
  const { data: ticketsData } = useSWR(selectedTeamId ? `/tickets?projectId=${selectedTeamId}&includeDeleted=true` : null, fetcher);
  const tickets = Array.isArray(ticketsData?.data) ? ticketsData.data : (Array.isArray(ticketsData) ? ticketsData : []);

  const [historySprints, setHistorySprints] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  
  const [remoteResults, setRemoteResults] = useState(null);
  const [isSearchingRemote, setIsSearchingRemote] = useState(false);

  useEffect(() => {
    if (selectedTeamId) {
      setHistorySprints([]);
      setPage(1);
      setHasMore(true);
      setRemoteResults(null);
      loadHistory(selectedTeamId, 1, true);
    }
  }, [selectedTeamId]);

  const loadHistory = async (teamId, pageNum, isReset = false) => {
    try {
      setIsLoadingHistory(true);
      const res = await api.get(`/projects/${teamId}/sprints?status=closed&page=${pageNum}&limit=10`);
      const sprintsList = Array.isArray(res.data.data) ? res.data.data : (res.data.sprints || res.data);
      
      if (Array.isArray(sprintsList)) {
        if (isReset) setHistorySprints(sprintsList);
        else setHistorySprints(prev => {
           const existingIds = prev.map(s => s._id);
           const newSprints = sprintsList.filter(s => !existingIds.includes(s._id));
           return [...prev, ...newSprints];
        });
        
        const pagination = res.data.pagination || { pages: 1 };
        setHasMore(pageNum < pagination.pages);
      } else {
        setHasMore(false);
      }
    } catch (e) {
      // Failed to load history
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const handleLoadMore = () => {
    if (!hasMore || isLoadingHistory) return;
    const nextPage = page + 1;
    setPage(nextPage);
    loadHistory(selectedTeamId, nextPage, false);
  };

  // Memoized local search results to avoid infinite loops and crashes
  const localResults = React.useMemo(() => {
    if (!searchQuery) return { sprints: [], tickets: [] };
    
    const q = searchQuery.toLowerCase();
    const cleanQuery = q.replace(/^vg-/, '').replace(/^#/, '').trim();
    
    // Match local sprints
    const matchedSprints = historySprints.filter(s => {
      const name = (s.name || '').toLowerCase();
      const startDate = s.startDate ? new Date(s.startDate).toLocaleDateString() : '';
      const endDate = s.endDate ? new Date(s.endDate).toLocaleDateString() : '';
      const dateStr = `${startDate} ${endDate}`.toLowerCase();
      return name.includes(q) || dateStr.includes(q);
    });
    
    // Match local tickets
    let matchedTickets = [];
    if (Array.isArray(tickets) && tickets.length > 0) {
      const hits = tickets.filter(t => {
        const title = (t.title || t.message || '').toLowerCase();
        const ticketId = String(t._id || '').toLowerCase();
        const ticketKey = String(t.key || '').toLowerCase().replace(/^vg-/, '').replace(/^#/, '');
        const creatorName = (t.creator?.name || '').toLowerCase();
        const resolverName = (t.resolver?.name || '').toLowerCase();
        return title.includes(q) || ticketKey.includes(cleanQuery) || ticketId.includes(cleanQuery) || creatorName.includes(q) || resolverName.includes(q);
      });
      
      // Populate sprint metadata for local tickets
      matchedTickets = hits.map(t => {
         const sId = typeof t.sprintId === 'object' ? t.sprintId?._id : t.sprintId;
         const sprintObj = historySprints.find(s => String(s._id) === String(sId));
         return { ...t, sprintId: sprintObj || t.sprintId };
      });
    }

    return { sprints: matchedSprints, tickets: matchedTickets };
  }, [searchQuery, historySprints, tickets]);

  // Debounced remote search
  useEffect(() => {
    if (!searchQuery || localResults.sprints.length > 0 || localResults.tickets.length > 0) {
      setIsSearchingRemote(false);
      return;
    }

    setIsSearchingRemote(true);
    const delay = setTimeout(async () => {
      try {
        const res = await api.get(`/projects/${selectedTeamId}/history-search?q=${encodeURIComponent(searchQuery)}`);
        setRemoteResults(res.data);
      } catch (err) {
        // Remote search fail
      } finally {
        setIsSearchingRemote(false);
      }
    }, 600);
    
    return () => clearTimeout(delay);
  }, [searchQuery, localResults.sprints.length, localResults.tickets.length, selectedTeamId]);

  const goToRemoteSprint = (sprintObj) => {
     setHistorySprints(prev => {
        if (!prev.find(s => s._id === sprintObj._id)) {
           return [sprintObj, ...prev];
        }
        return prev;
     });
     setSearchQuery('');
     setRemoteResults(null);
     setLocalResults({ sprints: [], tickets: [] });
     setExpandedSprintId(sprintObj._id);
  };

  const displaySprints = searchQuery ? localResults.sprints : historySprints;
  
  const allSearchTickets = React.useMemo(() => {
    if (!searchQuery) return [];
    const map = new Map();
    [...(localResults.tickets || []), ...(remoteResults?.tickets || [])].forEach(t => {
      if (t && t._id) map.set(String(t._id), t);
    });
    return Array.from(map.values());
  }, [searchQuery, localResults.tickets, remoteResults?.tickets]);
  
  const isSearchEmptyState = React.useMemo(() => {
    if (!searchQuery || isSearchingRemote) return false;
    return displaySprints.length === 0 && 
           allSearchTickets.length === 0 && 
           (!remoteResults?.sprints || remoteResults.sprints.length === 0);
  }, [searchQuery, isSearchingRemote, displaySprints.length, allSearchTickets.length, remoteResults?.sprints]);

  const handleDeleteSprint = (sprintId, e) => {
    e.stopPropagation();
    const sprint = historySprints?.find(s => s._id === sprintId);
    
    setConfModal({
      isOpen: true,
      title: t('confirmDeleteSprint'),
      message: t('confirmDeleteHistorySprintMsg'),
      expectedName: sprint?.name,
      onConfirm: async () => {
        try {
          await api.delete(`/sprints/${sprintId}`);
          setHistorySprints(prev => prev.filter(s => s._id !== sprintId));
          addToast(t('toastHistorySprintDeleted'), 'success');
        } catch (err) {
          // Delete fail
          addToast(t('toastError'), 'error');
        }
      }
    });
  };

  if (isLoadingTeams && teams.length === 0) {
    return <HistoryPageSkeleton t={t} />;
  }

  if (filteredProjects.length === 0 && isInitialized && !isLoadingTeams && !activeProjectId) {
    return (
      <div className="flex flex-col items-center justify-center h-[calc(100vh-160px)] space-y-8 animate-in fade-in duration-700">
        <div className="w-24 h-24 rounded-3xl bg-primary/10 flex items-center justify-center text-primary shadow-[0_0_50px_rgba(37,99,235,0.2)]">
           <RotateCcw size={48} strokeWidth={1.5} />
        </div>
        <div className="text-center max-w-md">
          <h2 className="text-3xl font-display font-black text-slate-100 mb-4">{t('noProjectsInWorkspace')}</h2>
          <p className="text-slate-500 text-lg leading-relaxed">
            {t('noProjectsInWorkspaceDesc')}
          </p>
        </div>
        <a 
          href="/projects" 
          className="px-8 py-4 bg-primary text-white rounded-2xl font-black uppercase tracking-widest text-sm hover:bg-blue-600 transition-all shadow-xl shadow-primary/30 hover:scale-105 active:scale-95"
        >
          {t('goProjects')}
        </a>
      </div>
    );
  }

  if (!selectedTeamId) {
    return <HistoryPageSkeleton t={t} />;
  }

  return (
    <div className="flex flex-col">
      <header className="flex-none mb-8 flex flex-col md:flex-row md:items-end justify-between gap-6 px-2">
        <div>
          <h1 className="text-4xl font-display font-black text-slate-100 flex items-center gap-4">
            <RotateCcw className="text-primary" size={32} /> {t('history')}
          </h1>
          <div className="flex items-center gap-4 mt-6">

              <div className="relative">
                <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
                <input 
                  type="text" 
                  placeholder={t('searchTicket') || "Buscar sprint o fecha..."} 
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="bg-white/5 border border-white/10 rounded-2xl pl-11 pr-5 py-4 text-white text-sm focus:border-primary transition-all focus:outline-none w-64 md:w-80"
                />
              </div>
          </div>
        </div>
      </header>

      <div className="space-y-6 pb-8">
         {isSearchingRemote && (
            <div className="space-y-4 mb-8">
               <h2 className="text-sm font-black uppercase tracking-widest text-primary border-b border-primary/20 pb-2 flex items-center gap-2">
                 <Loader2 size={14} className="animate-spin" />
                 {t('searchingServer')}
               </h2>
               {[1, 2, 3].map(i => <TicketHistorySkeleton key={i} />)}
            </div>
         )}
         
         {isSearchEmptyState && (
           <div className="flex flex-col items-center justify-center p-12 text-slate-500 bg-white/[0.02] border border-white/5 rounded-3xl mt-4">
             <div className="w-16 h-16 rounded-full bg-rose-500/10 flex items-center justify-center mb-4 border border-rose-500/20 text-rose-500">
                <Search size={24} />
             </div>
             <p className="font-bold text-sm text-white mb-2">{t('noResultsFound')}</p>
             <p className="text-xs balance text-center opacity-70">{t('noResultsDesc', { query: searchQuery })}</p>
           </div>
         )}
         
         {allSearchTickets.length > 0 && (
            <div className="space-y-4 mb-8">
               <h2 className="text-sm font-black uppercase tracking-widest text-primary border-b border-primary/20 pb-2">
                 {t('ticketsFound')} {localResults.tickets.length > 0 ? '' : t('remote')}
               </h2>
               
               {allSearchTickets.map(ticket => (
                  <motion.div 
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    key={ticket._id} 
                    className="bg-white/5 border border-white/10 p-6 rounded-2xl flex md:flex-row flex-col gap-6 justify-between items-start md:items-center shadow-lg group hover:border-primary/30 transition-all"
                  >
                     <div className="flex flex-col gap-2 flex-1">
                        <div className="flex items-center gap-3">
                           <div className="flex items-center gap-2">
                              <span className="text-[10px] font-black uppercase tracking-[0.2em] px-2 py-0.5 rounded-lg border bg-white/5 border-white/10 text-slate-400">
                                 {ticket.key || `VG-${ticket._id.substring(ticket._id.length - 8).toUpperCase()}`}
                              </span>
                              {ticket.planning?.score && (
                                <div className="flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-amber-500/10 text-amber-500 text-[9px] font-black border border-amber-500/20 shadow-sm shadow-amber-500/5">
                                  <Zap size={9} fill="currentColor" />
                                  {ticket.planning.score}
                                </div>
                              )}
                           </div>
                           <h3 className={`text-sm font-bold leading-tight line-clamp-2 ${ticket.isDeleted ? 'text-slate-500 line-through decoration-slate-600' : 'text-white'}`}>{ticket.title}</h3>
                           {ticket.isDeleted && (
                             <span className="flex items-center gap-1.5 px-2 py-0.5 rounded-lg bg-rose-500/10 border border-rose-500/20 text-[10px] font-black uppercase text-rose-500">
                               <AlertCircle size={10} />
                               {t('deleted')}
                             </span>
                           )}
                        </div>
                        {ticket.isDeleted && ticket.deletedAt && (
                          <p className="text-[10px] text-slate-500 font-bold italic">
                            {t('deletedAt')}: {new Date(ticket.deletedAt).toLocaleString(lang === 'es' ? 'es-ES' : 'en-US')}
                          </p>
                        )}
                        <div className="flex items-center gap-4 mt-2">
                           <div className="flex items-center gap-2">
                              <Layers size={12} className="text-slate-500" />
                              <span className="text-xs text-slate-400 font-medium italic">
                                {t('belongsToSprint')}: <span className="text-slate-300 font-bold not-italic">{ticket.sprintId?.name || t('noSprint')}</span>
                              </span>
                           </div>
                           {ticket.resolver && (
                              <div className="flex items-center gap-2 bg-emerald-500/5 px-2 py-1 rounded-lg border border-emerald-500/10">
                                 <div className="w-5 h-5 rounded-md overflow-hidden bg-emerald-500/20 flex items-center justify-center text-[8px] font-black text-emerald-500 shadow-sm border border-emerald-500/20">
                                    {ticket.resolver.avatar ? <img src={ticket.resolver.avatar} alt="" className="w-full h-full object-cover" /> : ticket.resolver.name?.[0] || 'R'}
                                 </div>
                                 <span className="text-[10px] font-bold text-emerald-400 uppercase tracking-tight">{ticket.resolver.name}</span>
                              </div>
                           )}
                        </div>
                     </div>
                     
                  </motion.div>
               ))}
            </div>
         )}
         
         {remoteResults?.sprints?.length > 0 && (
           <div className="space-y-4 mb-8">
             <h2 className="text-sm font-black uppercase tracking-widest text-primary border-b border-primary/20 pb-2">
               Sprints Encontrados (Remoto)
             </h2>
             {remoteResults.sprints.map(s => (
               <button 
                 key={s._id}
                 onClick={() => goToRemoteSprint(s)}
                 className="w-full text-left bg-white/[0.02] hover:bg-white/[0.05] border border-white/5 p-4 rounded-xl flex items-center justify-between transition-colors"
               >
                 <div className="flex items-center gap-3">
                   <RotateCcw size={14} className="text-primary/70" />
                   <span className="text-sm font-bold text-slate-200">{s.name}</span>
                 </div>
                 <ChevronDown size={16} className="text-slate-600" />
               </button>
             ))}
           </div>
         )}

         {isLoadingHistory && historySprints.length === 0 && !searchQuery ? (
           <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
             {[1, 2, 3, 4].map(i => <SprintHistoryCardSkeleton key={i} />)}
           </div>
         ) : !isSearchingRemote && !searchQuery && displaySprints.length === 0 ? (
           <div className="flex flex-col items-center justify-center h-64 text-slate-500">
             <div className="w-20 h-20 rounded-3xl bg-white/5 flex items-center justify-center mb-6 border border-white/5">
                <RotateCcw size={32} className="opacity-20" />
             </div>
             <p className="font-bold uppercase tracking-[0.2em] text-[10px] opacity-40">{t('noHistory')}</p>
           </div>
         ) : (
           <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
             {displaySprints.map(sprint => {
               let snapshot = null;
               try {
                 if (sprint.description && sprint.description.startsWith('{')) {
                   snapshot = JSON.parse(sprint.description);
                 }
               } catch (e) { /* Silent fail */ }

               const sprintTickets = snapshot ? snapshot.tickets : tickets.filter(t => String(t.sprintId) === String(sprint._id));
               const completed = snapshot ? snapshot.completed : sprintTickets.filter(t => ['done', 'completed'].includes(t.status)).length;
               const total = snapshot ? snapshot.total : sprintTickets.length;
                const initialPlanned = snapshot?.initialPlanned || total;
               const moved = snapshot ? (snapshot.moved || 0) : 0;
               const progress = total > 0 ? (completed / total) * 100 : 0;

               const isExpanded = expandedSprintId === sprint._id;

               return (
                 <motion.div 
                   layout
                   key={sprint._id}
                   className="bg-white/[0.03] border border-white/10 rounded-3xl p-8 relative overflow-hidden group hover:border-primary/30 transition-all shadow-xl hover:shadow-primary/5 flex flex-col h-fit"
                 >
                    <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full blur-3xl -mr-16 -mt-16 group-hover:bg-primary/10 transition-colors" />
                    
                    <div className="flex justify-between items-start mb-10">
                       <div className="space-y-3">
                          <div className="flex items-center gap-2">
                             <span className="w-1.5 h-1.5 rounded-full bg-slate-500" />
                             <h3 className="text-xl font-black text-white">{sprint.name}</h3>
                          </div>
                          <div className="flex items-center gap-3 text-[10px] font-bold text-slate-500 uppercase tracking-widest bg-white/5 px-3 py-1.5 rounded-lg border border-white/5">
                             <Calendar size={12} className="text-primary" />
                             <span>{new Date(sprint.startDate).toLocaleDateString()} - {new Date(sprint.endDate).toLocaleDateString()}</span>
                          </div>
                       </div>
                       <div className="text-right flex flex-col items-end gap-5">
                          <button 
                             onClick={(e) => handleDeleteSprint(sprint._id, e)}
                             className="p-2.5 rounded-xl bg-white/5 text-slate-500 opacity-0 group-hover:opacity-100 transition-all hover:bg-rose-500/10 hover:text-rose-500 hover:border-rose-500/20 border border-transparent z-20"
                          >
                             <Trash2 size={16} />
                          </button>
                          <div>
                             <div className="text-3xl font-display font-black text-white">{Math.round(progress)}%</div>
                             <div className="text-[9px] font-black text-primary uppercase tracking-widest mt-1 opacity-70">{t('completionRate')}</div>
                          </div>
                       </div>
                    </div>

                    <div className="space-y-8">
                       <div className="w-full h-2.5 bg-white/5 rounded-full overflow-hidden border border-white/5">
                          <motion.div 
                            initial={{ width: 0 }}
                            animate={{ width: `${progress}%` }}
                            transition={{ duration: 1, ease: 'easeOut' }}
                            className="h-full bg-primary shadow-[0_0_20px_rgba(37,99,235,0.4)]"
                          />
                       </div>

                       <div className="grid grid-cols-2 md:flex md:items-center gap-8 md:gap-12">
                           <div className="flex flex-col">
                              <span className="text-[10px] font-black text-slate-600 uppercase tracking-widest mb-2 italic">{t('planned')}</span>
                              <div className="flex items-center gap-3">
                                 <div className="w-8 h-8 rounded-xl bg-primary/10 flex items-center justify-center text-primary border border-primary/20">
                                    <Layers size={16} />
                                 </div>
                                 <span className="text-2xl font-black text-primary font-mono tracking-tighter">{initialPlanned}</span>
                              </div>
                           </div>
                           <div className="flex flex-col">
                              <span className="text-[10px] font-black text-slate-600 uppercase tracking-widest mb-2 italic">{t('completedTickets')}</span>
                             <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-xl bg-emerald-500/10 flex items-center justify-center text-emerald-500 border border-emerald-500/20">
                                   <CheckCircle2 size={16} />
                                </div>
                                <span className="text-2xl font-black text-emerald-500 font-mono tracking-tighter">{completed}</span>
                             </div>
                          </div>
                          <div className="flex flex-col">
                             <span className="text-[10px] font-black text-slate-600 uppercase tracking-widest mb-2 italic">{t('pendingTickets')}</span>
                             <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-xl bg-amber-500/10 flex items-center justify-center text-amber-500 border border-amber-500/20">
                                   <Clock size={16} />
                                </div>
                              <span className="text-2xl font-black text-amber-500 font-mono tracking-tighter">{total - completed}</span>
                             </div>
                          </div>
                          {snapshot && (
                            <div className="flex flex-col md:border-l md:border-white/10 md:pl-12">
                               <span className="text-[10px] font-black text-slate-600 uppercase tracking-widest mb-2 italic">{t('moved')}</span>
                               <div className="flex items-center gap-3">
                                  <div className="w-8 h-8 rounded-xl bg-rose-500/10 flex items-center justify-center text-rose-500 border border-rose-500/20">
                                     <ArrowRight size={16} />
                                  </div>
                                  <span className="text-2xl font-black text-rose-500 font-mono tracking-tighter">{moved}</span>
                               </div>
                            </div>
                          )}
                       </div>
                       
                       <button 
                         onClick={() => setExpandedSprintId(isExpanded ? null : sprint._id)}
                         className={`w-full py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all flex items-center justify-center gap-2 group ${
                           isExpanded ? 'bg-primary/10 text-primary border border-primary/20' : 'bg-white/5 border border-white/5 text-slate-500 hover:bg-primary/[0.08] hover:text-primary hover:border-primary/20'
                         }`}
                       >
                         {isExpanded ? t('hideDetails') : t('seeDetails')}
                         {isExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                       </button>

                       <AnimatePresence>
                         {isExpanded && (
                           <motion.div 
                             initial={{ opacity: 0, height: 0 }}
                             animate={{ opacity: 1, height: 'auto' }}
                             exit={{ opacity: 0, height: 0 }}
                             className="overflow-hidden space-y-3 pt-4 border-t border-white/5"
                           >
                              <div className="max-h-96 overflow-y-auto pr-2 custom-scrollbar space-y-3">
                                 {sprintTickets.map(ticket => (
                                   <div key={ticket._id} className="p-4 rounded-2xl bg-white/[0.02] border border-white/5 flex items-center justify-between group/ticket hover:bg-white/[0.04] transition-all">
                                      <div className="flex items-center gap-4">
                                         <div className={`p-2 rounded-lg ${ticket.status === 'done' ? 'bg-emerald-500/10 text-emerald-500' : 'bg-slate-500/10 text-slate-400'}`}>
                                           {ticket.status === 'done' ? <CheckCircle2 size={14} /> : <Clock size={14} />}
                                         </div>
                                         <div>
                                            <div className="flex items-center gap-2 mb-0.5">
                                               <span className="text-[9px] font-black text-primary uppercase tracking-tighter">{ticket.key}</span>
                                               <p className="text-sm font-bold text-white line-clamp-1">{ticket.title}</p>
                                               {ticket.resolver && (
                                                  <div className="flex items-center gap-1.5 ml-2">
                                                     <div className="w-4 h-4 rounded-md overflow-hidden bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-[7px] font-black text-emerald-500">
                                                        {ticket.resolver.avatar ? <img src={ticket.resolver.avatar} alt="" className="w-full h-full object-cover" /> : ticket.resolver.name?.[0] || 'R'}
                                                     </div>
                                                  </div>
                                               )}
                                            </div>
                                             <p className="text-[10px] text-slate-500 uppercase font-black flex items-center gap-2">
                                               {ticket.isDeleted ? (
                                                  <>
                                                   <span className="text-rose-500 font-black">{t('deleted')}</span>
                                                   {ticket.deletedAt && (
                                                     <span className="opacity-50 font-bold ml-1.5">• {new Date(ticket.deletedAt).toLocaleString(lang === 'es' ? 'es-ES' : 'en-US')}</span>
                                                   )}
                                                  </>
                                               ) : (
                                                 ticket.status === 'done' 
                                                   ? (
                                                     <span className="flex items-center gap-1.5">
                                                       {t('ticketResolved') || 'Ticket Resuelto'}
                                                       {ticket.resolver && <span className="text-emerald-500/60 lowercase italic font-medium">by {ticket.resolver.name}</span>}
                                                     </span>
                                                   )
                                                   : (
                                                      <span className="flex items-center gap-1.5 font-bold uppercase tracking-wide opacity-80">
                                                        {ticket.movedTo === 'backlog' && t('movedToBacklog')}
                                                        {ticket.movedTo === 'orphaned_review' && t('waitingForNextSprint')}
                                                        {ticket.movedTo && ticket.movedTo !== 'backlog' && ticket.movedTo !== 'orphaned_review' && (
                                                          <>
                                                            {t('movedTo')} <span className="text-primary/70">{ticket.movedTo}</span>
                                                          </>
                                                        )}
                                                        {!ticket.movedTo && (snapshot?.movedToNext ? t('movedToNextSprint') : t('movedToBacklog'))}
                                                      </span>
                                                    )
                                               )}
                                             </p>
                                         </div>
                                      </div>
                                   </div>
                                 ))}
                              </div>
                           </motion.div>
                         )}
                       </AnimatePresence>
                    </div>
                 </motion.div>
               );
             })}
           </div>
         )}
         
         {hasMore && historySprints.length > 0 && !searchQuery && !isSearchingRemote && !remoteResults && (
            <div className="flex justify-center pt-8 pb-4">
              <button
                onClick={handleLoadMore}
                disabled={isLoadingHistory}
                className="px-8 py-4 bg-white/[0.03] border border-white/10 rounded-2xl text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 hover:text-white hover:bg-white/[0.08] hover:border-white/20 transition-all disabled:opacity-50 flex items-center gap-3 active:scale-95"
              >
                {isLoadingHistory ? (
                  <>
                    <div className="w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
                    <span>{t('loading')}</span>
                  </>
                ) : (
                  <>
                    <span>{t('loadMoreSprints')}</span>
                    <ArrowRight size={14} className="opacity-50" />
                  </>
                )}
              </button>
            </div>
         )}
      </div>

      <ConfirmationModal 
        {...confModal} 
        onClose={() => setConfModal({ ...confModal, isOpen: false })} 
        t={t} 
      />
    </div>
  );
};

export default History;
